/**
 * @file index.tsx
 *
 * @fileoverview Renders a simple facet component that has no search functionality built-in.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Facet } from '~/jobiqo/models/search';
import FacetMultiple from '~/jobiqo/components/Search/facets/facet-multiple';
import FacetSingle from '~/jobiqo/components/Search/facets/facet-single';
import { FacetPopover } from '~/vcl/molecules/07-search/02-facet/02-facet-popover';
import { Badge } from '~/vcl/atoms/03-badge';

interface SimpleFacetProps {
  /**
   * The current querystring object.
   */
  querystring: any;
  /**
   * The current pathname.
   */
  pathname: string;
  /**
   * The current facet.
   */
  facet: Facet;
  /**
   * The callback for when toggling a filter.
   */
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  /**
   * Gets the list of selected facets.
   */
  getFacetSelectedItems(facet: Facet): string[];
}
export default function SimpleFacet({
  facet,
  onFacetToggle,
  getFacetSelectedItems,
  querystring,
  pathname,
}: SimpleFacetProps) {
  const intl = useIntl();
  const [openedFacet, setOpenedFacet] = useState<string>('');

  return (
    <FacetPopover
      onOutsideClick={() => setOpenedFacet('')}
      onToggle={(opened) => {
        if (!opened) {
          setOpenedFacet('');
        }
      }}
      onTriggerClick={() =>
        setOpenedFacet(openedFacet === facet.name ? '' : facet.name)
      }
      opened={openedFacet === facet.name}
      label={
        facet.title ? (
          <>
            <span>
              {!!intl.messages &&
              (intl.messages?.[`config_facet_${facet.name}_title`] as string)
                ? (intl.messages[`config_facet_${facet.name}_title`] as string)
                : facet.title}
            </span>
            {getFacetSelectedItems(facet).length > 0 ? (
              <span>
                &nbsp;
                <Badge>{getFacetSelectedItems(facet).length}</Badge>
              </span>
            ) : null}
          </>
        ) : (
          facet.name
        )
      }
    >
      {facet.multiple ? (
        <FacetMultiple
          querystring={querystring}
          onFacetToggle={(facetItem, selectedItems, alias) =>
            onFacetToggle(facetItem, selectedItems, alias)
          }
          selectedItems={getFacetSelectedItems(facet)}
          facet={facet}
        />
      ) : (
        <FacetSingle
          querystring={querystring}
          pathname={pathname}
          selectedItems={getFacetSelectedItems(facet)}
          facet={facet}
          onFacetToggle={(facetItem, selectedItems, alias) =>
            onFacetToggle(facetItem, selectedItems, alias)
          }
        />
      )}
    </FacetPopover>
  );
}
